import {t, useLanguage} from './i18n';
import {zoneLabel} from './utils/regionalCities';
import RegionalFields from "./components/RegionalFields";
import {markets,getMarket,readSearchMarket} from "./utils/market";
import {trackBusinessFunnel} from './utils/businessFunnel';
import {searchPanamaLocations} from './utils/panamaLocationSearch';
// src/NewBusinessPage.js
// Private three-step onboarding; all platforms use the authenticated server creation path.
// LOOP NEW BUSINESS MINIMAL SETUP V2.3 — shared country phone picker + Instagram synced to business profile.
// Mantiene el flujo rápido y el draft/login redirect de la fase simple, pero con una UI más minimalista y fiel a Loop.

import {callCallableNativeSafe} from "./utils/callableNativeSafe";
import "./NewBusinessOnboarding.css";
import React, { useEffect, useMemo, useState, useId, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { auth } from "./firebase";

import CustomerImportModal from "./components/CustomerImportModal";
import { PhoneInput } from "./components/PhoneKit";
import { getBusinessRouteParam } from "./utils/businessSlug";
import { parsePhoneValue } from "./utils/phoneUtils";

import { getEffectiveManualAuthSession, getManualAuth, isManualAuthLikelyValid } from "./utils/manualAuth";


const draftKey = () => 'loop_new_business_v3_' + (auth?.currentUser?.uid || resolveManualBusinessUser()?.uid || 'guest');

const BUSINESS_TYPES = [
  { id: "beauty", label: "Belleza & Barberías", hint: "Salones, barberías, uñas, cejas y maquillaje." },
  { id: "wellness", label: "Bienestar & Spa", hint: "Spa, masajes, terapias y bienestar." },
  { id: "events", label: "Eventos", hint: "Cumpleaños, shows, fotografía y experiencias." },
  { id: "fitness", label: "Fitness & Clases", hint: "Entrenadores, clases y actividades." },
  { id: "others", label: "Otro", hint: "Servicios con citas, agenda o reservas." },
];

const SERVICE_MODES = [
  { id: "onsite", label: "En mi local", hint: "Los clientes van a tu ubicación." },
  { id: "delivery", label: "A domicilio", hint: "Atiendes en la ubicación del cliente." },
  { id: "hybrid", label: "Ambos", hint: "Tienes local y también atiendes a domicilio." },
  { id: "online", label: "Online / remoto", hint: "Atiendes por llamada, videollamada o plataforma." },
];



const STEPS = [{key:'business',label:'Tu negocio'},{key:'location',label:'Dónde atiendes'},{key:'contact',label:'Contacto y revisión'}];

const emptyForm = {
  requestId: "",
  countryCode: "PA",
  timezone: "America/Panama",
  city: "",
  teamMode: "solo",
  name: "",
  type: "",
  serviceMode: "onsite",
  locationText: "",
  locationOption: null,
  coverageText: "",
  coverageOption: null,
  phone: "",
  instagram: "",
  software: "",
  softwareOther: "",
};

function sanitize(value) {
  return value == null ? "" : String(value).trim();
}

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function normalizeBusinessContactPhone(value = "") {
  const parsed = parsePhoneValue(value, { defaultCountry: "PA" });
  const raw = sanitize(value);

  if (!parsed.e164 || !parsed.isValid) {
    return {
      raw,
      national: parsed.nationalNumber || digitsOnly(raw),
      nationalNumber: parsed.nationalNumber || digitsOnly(raw),
      nationalFormatted: parsed.nationalFormatted || raw,
      countryCode: parsed.countryCode || "PA",
      dialCode: parsed.dialCode || "+507",
      countryLabel: parsed.countryLabel || "Panamá",
      e164: "",
      display: parsed.display || raw,
      isValid: false,
    };
  }

  return {
    raw,
    national: parsed.nationalNumber,
    nationalNumber: parsed.nationalNumber,
    nationalFormatted: parsed.nationalFormatted,
    countryCode: parsed.countryCode,
    dialCode: parsed.dialCode,
    countryLabel: parsed.countryLabel,
    e164: parsed.e164,
    display: parsed.display,
    isValid: true,
  };
}

function normalizeInstagramHandle(value = "") {
  const raw = sanitize(value)
    .replace(/^https?:\/\/(www\.)?instagram\.com\//i, "")
    .replace(/^instagram\.com\//i, "")
    .replace(/^@+/, "")
    .split(/[/?#]/)[0]
    .trim();
  return raw.replace(/[^a-zA-Z0-9._]/g, "").slice(0, 30);
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}



function locationLabel(option) {
  if (!option) return "";
  return option.label || [option.province, option.district, option.corregimiento, option.zone].filter(Boolean).join(", ");
}

export function readDraft(prefill = {}) {
  try {
    let raw = window.localStorage.getItem(draftKey());
    if (!raw && draftKey() !== 'loop_new_business_v3_guest' && window.sessionStorage.getItem('loop_business_guest_handoff') === 'yes') {
      raw=window.localStorage.getItem('loop_new_business_v3_guest');
      if (raw) window.localStorage.setItem(draftKey(), raw);
      window.localStorage.removeItem('loop_new_business_v3_guest');
      window.sessionStorage.removeItem('loop_business_guest_handoff');
    }
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return { ...emptyForm, ...parsed };
    }
  } catch (_) {}

  const market=readSearchMarket();
  return {
    ...emptyForm,
    countryCode:market.code,timezone:market.zones[0],
    name: prefill.name || "",
    type: prefill.type || prefill.businessType || "",
    serviceMode: prefill.serviceMode || "onsite",
    locationText: prefill.address || prefill.locationText || "",
    locationOption: prefill.locationOption || prefill.location || null,
    coverageText: prefill.coverageArea || "",
    coverageOption: prefill.coverageOption || null,
    phone: prefill.phone || prefill.whatsapp || prefill.publicPhone || "",
    instagram: prefill.instagram || prefill.instagramHandle || prefill.publicInstagram || "",
    software: prefill.software || "",
    softwareOther: prefill.softwareOther || "",
  };
}

function saveDraft(form) {
  try {
    window.localStorage.setItem(draftKey(), JSON.stringify(form || {}));
  } catch (_) {}
}

function clearDraft() {
  try {
    window.localStorage.removeItem(draftKey());
  } catch (_) {}
}

function resolveManualBusinessUser() {
  try {
    const session = typeof getEffectiveManualAuthSession === "function" ? getEffectiveManualAuthSession() : null;
    if (session?.loggedIn && session?.user?.uid) {
      return {
        uid: session.user.uid,
        email: session.user.email || session.manualAuth?.email || "",
        displayName: session.user.displayName || session.manualAuth?.displayName || session.manualAuth?.name || "",
        source: session.source || session.manualAuth?.source || "manual-auth",
      };
    }
  } catch (_) {}

  try {
    const ma = typeof getManualAuth === "function" ? getManualAuth() : null;
    const valid = ma && (!isManualAuthLikelyValid || isManualAuthLikelyValid(ma));
    if (valid && ma.uid) {
      return {
        uid: ma.uid,
        email: ma.email || ma.raw?.email || "",
        displayName: ma.displayName || ma.name || ma.raw?.displayName || "",
        source: ma.source || "manual-auth",
      };
    }
  } catch (_) {}

  return null;
}

function normalizeLoginTarget() {
  try {
    return `${window.location.pathname || "/new-business"}${window.location.search || ""}`;
  } catch (_) {
    return "/new-business";
  }
}

function RadioRow({ active, title, copy, onClick }) {
  return (
    <button type="button" className={`nb-min-row ${active ? "is-active" : ""}`} aria-pressed={active} onClick={onClick}>
      <span className="nb-min-radio" aria-hidden="true"><i /></span>
      <span className="nb-min-row-copy">
        <strong>{t(title)}</strong>
        {copy ? <small>{t(copy)}</small> : null}
      </span>
    </button>
  );
}

export function LocationSearchInput({ countryCode = "PA", label, value, selected, onTextChange, onSelect, placeholder = "Ej: Costa del Este" }) {
  const [focused, setFocused] = useState(false);
  const [highlight,setHighlight]=useState(-1);
  const id=useId();
  const normalized=normalizeText(value);
  const suggestions=useMemo(()=>countryCode === "PA" ? searchPanamaLocations(normalized,4) : [],[normalized,countryCode]);
  const manual=String(value || '').trim();
  const options=manual ? [...suggestions,{id:'manual',zone:'Usar esta ubicación',manual:true,label:manual}] : suggestions;
  const open=focused && options.length>0;
  const choose=option=>{if(option.manual)onTextChange(manual);else onSelect(option);setFocused(false);setHighlight(-1);};
  return <div className="nb-min-location-field">
    <label className="nb-min-field"><span>{label}</span><input role="combobox" aria-expanded={open} aria-controls={id+'-list'} aria-autocomplete="list" aria-describedby={id+'-hint'} aria-activedescendant={open && highlight>=0 ? id+'-'+highlight : undefined}
      value={value || ''} maxLength={300} onChange={e=>{onTextChange(e.target.value);setHighlight(-1);setFocused(true);}}
      onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)} placeholder={placeholder} autoComplete="off"
      onKeyDown={e=>{
        if(e.key==='Escape'){setFocused(false);setHighlight(-1);return;}
        if(e.key==='ArrowDown' || e.key==='ArrowUp'){e.preventDefault();setFocused(true);if(options.length)setHighlight(h=>e.key==='ArrowDown'?Math.min(h+1,options.length-1):Math.max(h-1,0));}
        if(e.key==='Enter' && open && highlight>=0){e.preventDefault();choose(options[highlight]);}
      }}/></label>
    {open && <div id={id+'-list'} role="listbox" aria-label="Zonas sugeridas" className="nb-min-location-suggestions">{options.map((option,index)=><button type="button" role="option" className={option.manual?"nb-min-location-manual":undefined} tabIndex={-1} id={id+'-'+index} aria-selected={highlight===index} key={option.id} onMouseDown={e=>e.preventDefault()} onClick={()=>choose(option)}><strong>{option.zone || option.corregimiento}</strong><span>{option.manual?option.label:[...new Set([option.corregimiento,option.district,option.province].filter(part=>part!==option.zone))].filter(Boolean).join(' · ')}</span></button>)}</div>}
    <small id={id+'-hint'} className="nb-min-field-hint">{selected ? t("✓ Zona seleccionada. Puedes cambiarla cuando quieras.") : value && !suggestions.length ? t("Puedes usar esta zona escrita, aunque no aparezca en las sugerencias.") : t("Elige una sugerencia o escribe tu zona. La dirección exacta se completa después.")}</small>
  </div>;
}

function MiniProgress({ step, onBack }) {
  return <nav className="nb-min-progress" aria-label={t("Pasos del registro")}><ol>{STEPS.map((item,index)=><li key={item.key}><button type="button" aria-current={index===step?'step':undefined} disabled={index>step} onClick={()=>onBack(index)}><span>{index<step?'✓':index+1}</span>{t(item.label)}</button></li>)}</ol><p>{t('Paso {step} de {total}',{step:step+1,total:STEPS.length})}</p></nav>;
}

function PostCreationChecklist({ businessId, summary = {}, onEditProfile, onBackHome }) {


  return (
    <main className="nb-min-page nb-min-created-page">

      <section className="nb-min-created-shell is-submitted">
        <button type="button" className="nb-min-back" onClick={onBackHome}>← Inicio</button>

        <header className="nb-min-created-hero">
          <span>Negocio privado</span>
          <h1>Tu negocio ya tiene su espacio.</h1>
          <p>
            Creamos <strong>{summary.businessName || "tu negocio"}</strong>. Todavía no está publicado ni enviado a revisión.</p>
          <p>
            Completa tu perfil, agrega un servicio y confirma tus horarios. Después podrás enviarlo a revisión desde el editor.
          </p>
          <div className="nb-min-created-actions">
            <button type="button" className="nb-min-primary" onClick={onEditProfile}>Completar mi negocio</button>
          </div>
          <div className="nb-min-created-meta">
            <small>{summary.businessTypeLabel || "Perfil privado"}</small>
            <small>{summary.serviceModeLabel || "Solicitud enviada"}</small>
            <small>ID {String(businessId || "").slice(0, 8)}</small>
          </div>

        </header>
      </section>
    </main>
  );
}

export default function NewBusinessPage({ user = null, prefill = {} }) {
  useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const resumedAuth = useRef(false);
  const startedRegistration = useRef(false);
  const errorRef=useRef(null);
  const headingRef=useRef(null);
  const [form, setForm] = useState(() => readDraft(prefill));
  const [formOwner,setFormOwner]=useState(draftKey);
  const [statusMessage, setStatusMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createdBusinessId, setCreatedBusinessId] = useState(null);
  const [createdBusinessSummary, setCreatedBusinessSummary] = useState(null);
  const [customerImportOpen, setCustomerImportOpen] = useState(false);
  const [manualAuthUser, setManualAuthUser] = useState(() => resolveManualBusinessUser());

  useEffect(() => {
    const syncManualAuth = () => setManualAuthUser(resolveManualBusinessUser());
    syncManualAuth();
    try {
      window.addEventListener("manual-auth-changed", syncManualAuth);
      window.addEventListener("manual-auth-detected", syncManualAuth);
      window.addEventListener("loop-auth-login-success", syncManualAuth);
      window.addEventListener("loop-auth-session-changed", syncManualAuth);
      window.addEventListener("manual-auth-cleared", syncManualAuth);
      window.addEventListener("loop-auth-cleared", syncManualAuth);
    } catch (_) {}
    return () => {
      try {
        window.removeEventListener("manual-auth-changed", syncManualAuth);
        window.removeEventListener("manual-auth-detected", syncManualAuth);
        window.removeEventListener("loop-auth-login-success", syncManualAuth);
        window.removeEventListener("loop-auth-session-changed", syncManualAuth);
        window.removeEventListener("manual-auth-cleared", syncManualAuth);
        window.removeEventListener("loop-auth-cleared", syncManualAuth);
      } catch (_) {}
    };
  }, []);

  const currentUser = user || auth?.currentUser || manualAuthUser || null;
  useEffect(() => {
    if (!currentUser?.uid || resumedAuth.current) return;
    try {
      if (sessionStorage.getItem('loop_business_resume_step') === '1' && form.name && form.type && formOwner === draftKey()) {
        resumedAuth.current = true;
        sessionStorage.removeItem('loop_business_resume_step');
        setStep(1);
        void trackBusinessFunnel('business_auth_completed', {search: window.location.search});
      }
    } catch (_) {}
  }, [currentUser?.uid, form.name, form.type, formOwner]);
  useEffect(() => { void trackBusinessFunnel('business_registration_view', {search: window.location.search}); }, []);
  const selectedType = useMemo(() => BUSINESS_TYPES.find((type) => type.id === form.type) || null, [form.type]);
  const selectedMode = useMemo(() => SERVICE_MODES.find((mode) => mode.id === form.serviceMode) || null, [form.serviceMode]);

  useEffect(() => {
    const key=draftKey();
    if(formOwner!==key) {setForm(readDraft());setFormOwner(key);setStep(0);return;}
    if(!createdBusinessId) saveDraft(form);
  }, [form,formOwner,currentUser?.uid,createdBusinessId]);

  useEffect(() => {
    try { window.scrollTo({ top: 0, behavior: "auto" }); headingRef.current?.focus({preventScroll:true}); } catch (_) {}
  }, [step]);

  useEffect(()=>{if(statusMessage?.type==='error')errorRef.current?.focus();},[statusMessage]);
  const changeStep=value=>{setStatusMessage(null);setStep(value);};
  const update = (key, value) => {
    if (!startedRegistration.current) {
      startedRegistration.current = true;
      void trackBusinessFunnel('business_registration_started', {search: window.location.search});
    }
    setStatusMessage(null);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateStep = () => {
    if (step === 0 && !sanitize(form.name)) return t("Escribe el nombre de tu negocio.");
    if (step === 0 && !form.type) return t("Selecciona el tipo de negocio.");
    if (step === 1 && form.countryCode !== "PA" && !sanitize(form.city)) return t("Escribe la ciudad del negocio.");
    if (step === 1 && !form.serviceMode) return t("Selecciona cómo atiendes a tus clientes.");
    if (step === 1 && form.serviceMode !== "online") {
      const needsLocalZone = form.serviceMode === "onsite" || form.serviceMode === "hybrid";
      const needsCoverageZone = form.serviceMode === "delivery" || form.serviceMode === "hybrid";
      if (needsLocalZone && !sanitize(form.locationText)) return t("Escribe la zona donde está tu negocio.");
      if (needsCoverageZone && !sanitize(form.coverageText)) return t("Escribe al menos una zona donde atiendes.");
    }
    if (step === 2) {
      const phone = normalizeBusinessContactPhone(form.phone);
      if (!phone.e164) return t("Agrega un teléfono o WhatsApp válido para el país seleccionado.");
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) {
      setStatusMessage({ type: "error", text: err });
      return;
    }
    void trackBusinessFunnel(step === 0 ? 'business_basics_completed' : 'business_location_completed', {search: window.location.search});
    if (step === 0 && !(currentUser || auth?.currentUser || resolveManualBusinessUser())?.uid) {
      try { sessionStorage.setItem('loop_business_resume_step', '1'); } catch (_) {}
      goToLoginKeepingDraft(true);
      return;
    }
    setStep((value) => Math.min(value + 1, STEPS.length - 1));
  };

  const back = () => {
    setStatusMessage(null);
    setStep((value) => Math.max(value - 1, 0));
  };

  const goToLoginKeepingDraft = (register = false) => {
    saveDraft(form);
    try { if(draftKey() === 'loop_new_business_v3_guest') window.sessionStorage.setItem('loop_business_guest_handoff','yes'); } catch (_) {}
    const nextTarget = normalizeLoginTarget();
    try {
      window.localStorage.setItem("loop_post_auth_redirect", nextTarget);
      window.sessionStorage.setItem("loop_post_auth_redirect", nextTarget);
    } catch (_) {}
    void trackBusinessFunnel('business_auth_started', {search: window.location.search});
    navigate(`/${register ? 'register' : 'login'}?next=${encodeURIComponent(nextTarget)}`, {
      state: {
        from: nextTarget,
        info: "Crea tu cuenta o inicia sesión para continuar. Conservaremos lo que escribiste en este dispositivo.",
      },
    });
  };

  async function handleCreate() {
    if(saving) return;
    setStatusMessage(null);

    if (!sanitize(form.name)) {
      setStep(0);
      setStatusMessage({ type: "error", text: t("Escribe el nombre de tu negocio.") });
      return;
    }

    if (!form.type) {
      setStep(0);
      setStatusMessage({ type: "error", text: t("Selecciona el tipo de negocio.") });
      return;
    }

    if (!form.serviceMode) {
      setStep(1);
      setStatusMessage({ type: "error", text: t("Selecciona cómo atiendes a tus clientes.") });
      return;
    }

    const contactPhone = normalizeBusinessContactPhone(form.phone);
    if (!contactPhone.e164) {
      setStep(2);
      setStatusMessage({ type: "error", text: t("Agrega un teléfono o WhatsApp válido para el país seleccionado.") });
      return;
    }

    const effectiveUser = currentUser || auth?.currentUser || resolveManualBusinessUser();
    if (!effectiveUser || !effectiveUser.uid) {
      goToLoginKeepingDraft();
      return;
    }

    setSaving(true);
    void trackBusinessFunnel('business_contact_completed', {search: window.location.search});
    const requestId = form.requestId || window.crypto.randomUUID();
    const submittedForm = {...form, requestId};
    setForm(submittedForm); saveDraft(submittedForm);
    try {
      const response = await callCallableNativeSafe({functionName:'createBusinessDraftV3',requireAuth:true,data:{form:submittedForm,businessId:requestId}});
      const result=response?.data || response;
      if (!result?.ok || !result.businessId) throw new Error('No pudimos confirmar la creación. Intenta nuevamente.');
      void trackBusinessFunnel('business_created',{businessId:result.businessId,search:window.location.search});
      clearDraft();
      navigate(`/business/${encodeURIComponent(result.businessId)}/edit?tab=services&setup=1`, {replace:true});
    } catch(error) {setStatusMessage({type:'error',text:error?.message || 'No pudimos crear el negocio. Tu avance sigue guardado.'});}
    finally {setSaving(false);}
  }

  if (createdBusinessId && createdBusinessSummary) {
    const safeBusinessRouteParam = encodeURIComponent(getBusinessRouteParam({ id: createdBusinessId, ...createdBusinessSummary }) || createdBusinessId);
    return (
      <>
        <PostCreationChecklist
          businessId={createdBusinessId}
          summary={createdBusinessSummary}
          onOpenDashboard={() => navigate(`/business/${safeBusinessRouteParam}/calendar`)}
          onEditProfile={() => navigate(`/business/${safeBusinessRouteParam}/edit`)}
          onImportClients={() => setCustomerImportOpen(true)}
          onBackHome={() => navigate("/")}
        />
        <CustomerImportModal
          open={customerImportOpen}
          businessId={createdBusinessId}
          onClose={() => setCustomerImportOpen(false)}
        />
      </>
    );
  }

  const continueLabel = step === STEPS.length - 1
    ? saving
      ? t("Guardando avance...")
      : currentUser
        ? t("Continuar con mis servicios")
        : t("Iniciar sesión y continuar")
    : step === 0 && !currentUser ? t("Guardar y continuar") : t("Continuar");

  return (
    <main className="nb-min-page">

      <section className="nb-min-shell">
        <button type="button" className="nb-min-back" onClick={() => step === 0 ? navigate(-1) : back()}>
          {step === 0 ? t("← Volver") : t("← Atrás")}
        </button>

        <header className="nb-min-header">
          <span>{t("Loop para negocios")}</span>
          <h1 ref={headingRef} tabIndex={-1}>{t("Empecemos con tu negocio")}</h1>
          <p>{t("Completa lo básico. Puedes terminar de configurarlo después.")}</p>
        </header>

        <MiniProgress step={step} onBack={changeStep} />

        <section className="nb-min-card">
          {statusMessage ? <div ref={errorRef} tabIndex={-1} role="alert" className={`nb-min-alert ${statusMessage.type === "success" ? "success" : "error"}`}>{statusMessage.text}</div> : null}

          {step === 0 ? (
            <div className="nb-min-question">
              <h2>{t("¿Cómo se llama tu negocio?")}</h2>
              <label className="nb-min-field">
                <span>{t("Nombre del negocio")}</span>
                <input maxLength={180} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder={t("Ej: Studio Glow")} autoFocus />
              </label>
            </div>
          ) : null}

          {step === 0 ? (
            <div className="nb-min-question">
              <h2>{t("Especialidad")}</h2>
              <div className="nb-min-list">
                {BUSINESS_TYPES.filter(type => ['beauty','wellness','events'].includes(type.id)).map((type) => (
                  <RadioRow
                    key={type.id}
                    active={form.type === type.id}
                    title={type.label}
                    copy={type.hint}
                    onClick={() => update("type", type.id)}
                  />
                ))}
              </div>
              {form.type === 'events' && <p className="nb-min-field-hint">Después podrás crear paquetes de show o visita, sus duraciones, personajes y extras. Si vas al lugar del evento, elige atención a domicilio en el siguiente paso.</p>}
            </div>
          ) : null}

          {step === 2 && <div className="nb-min-question"><h2>{t("¿Cómo trabajas?")}</h2><div className="nb-team-options" role="group" aria-label={t("Equipo")}><button type="button" aria-pressed={form.teamMode!=='team'} onClick={()=>update('teamMode','solo')}>{t("Por mi cuenta")}</button><button type="button" aria-pressed={form.teamMode==='team'} onClick={()=>update('teamMode','team')}>{t("Con un equipo")}</button></div><small className="nb-min-field-hint">{t("Podrás agregar personas y permisos más adelante.")}</small></div>}
          {step === 1 ? (
            <div className="nb-min-question">
              <h2>{t("¿Cómo atiendes?")}</h2>
              <div className="nb-min-list">
                {SERVICE_MODES.filter(mode => mode.id !== 'online').map((mode) => (
                  <RadioRow
                    key={mode.id}
                    active={form.serviceMode === mode.id}
                    title={mode.label}
                    copy={mode.hint}
                    onClick={() => update("serviceMode", mode.id)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {step === 1 ? (
            <div className="nb-min-question">
              <h2>{form.serviceMode === "delivery" ? t("¿En qué zona atiendes?") : form.serviceMode === "online" ? "¿Dónde atiendes online?" : t("¿Dónde está ubicado tu negocio?")}</h2>

              <RegionalFields countryCode={form.countryCode || 'PA'} city={form.city || ''} timezone={form.timezone} onChange={patch=>setForm(prev=>({...prev,...patch,...(patch.countryCode && patch.countryCode!==prev.countryCode ? {locationText:'',locationOption:null,coverageText:'',coverageOption:null} : {})}))}/>
              {form.serviceMode === "delivery" ? (
                <LocationSearchInput countryCode={form.countryCode || "PA"}
                  label={t("Zona principal de cobertura")}
                  value={form.coverageText}
                  selected={form.coverageOption}
                  onTextChange={(value) => setForm((prev) => ({ ...prev, coverageText: value, coverageOption: null }))}
                  onSelect={(option) => setForm((prev) => ({ ...prev, coverageText: locationLabel(option), coverageOption: option }))}
                  placeholder={form.countryCode === "PA" ? "Ej: Costa del Este" : t("Barrio, zona o referencia")}
                />
              ) : null}
              {form.serviceMode === "hybrid" ? (
                <div className="nb-min-location-stack">
                  <LocationSearchInput countryCode={form.countryCode || "PA"}
                    label={t("Zona del local")}
                    value={form.locationText}
                    selected={form.locationOption}
                    onTextChange={(value) => setForm((prev) => ({ ...prev, locationText: value, locationOption: null }))}
                    onSelect={(option) => setForm((prev) => ({ ...prev, locationText: locationLabel(option), locationOption: option }))}
                    placeholder={form.countryCode === "PA" ? "Ej: Costa del Este" : t("Barrio, zona o referencia")}
                  />
                  <LocationSearchInput countryCode={form.countryCode || "PA"}
                    label={t("Zona principal de cobertura")}
                    value={form.coverageText}
                    selected={form.coverageOption}
                    onTextChange={(value) => setForm((prev) => ({ ...prev, coverageText: value, coverageOption: null }))}
                    onSelect={(option) => setForm((prev) => ({ ...prev, coverageText: locationLabel(option), coverageOption: option }))}
                    placeholder={t("Barrio, zona o referencia")}
                  />
                </div>
              ) : null}
              {form.serviceMode === "onsite" ? (
                <LocationSearchInput countryCode={form.countryCode || "PA"}
                  label={t("Zona del negocio")}
                  value={form.locationText}
                  selected={form.locationOption}
                  onTextChange={(value) => setForm((prev) => ({ ...prev, locationText: value, locationOption: null }))}
                  onSelect={(option) => setForm((prev) => ({ ...prev, locationText: locationLabel(option), locationOption: option }))}
                  placeholder={form.countryCode === "PA" ? "Ej: Costa del Este" : t("Barrio, zona o referencia")}
                />
              ) : null}
              {form.serviceMode === "online" ? (
                <label className="nb-min-field">
                  <span>Plataforma o referencia</span>
                  <input value={form.locationText} onChange={(e) => update("locationText", e.target.value)} placeholder="Ej: Zoom, Google Meet, WhatsApp" />
                </label>
              ) : null}
            </div>
          ) : null}

          {step === 2 ? (
            <div className="nb-min-question">
              <h2>{t("¿Cómo pueden contactarte?")}</h2>
              <p className="nb-min-soft-copy">{t("Este teléfono aparecerá en tu perfil público. Usaremos el correo de tu cuenta para comunicarnos contigo; no lo publicaremos.")}</p>
              <div className="nb-min-contact-grid">
                <div className="nb-min-business-phone-field">
                  <PhoneInput
                    label={t("Teléfono / WhatsApp del negocio")}
                    value={form.phone}
                    defaultCountry={form.countryCode || "PA"}
                    required
                    onChange={(display, parsed) => {
                      update("phone", parsed?.display || display || "");
                    }}
                    hint={
                      normalizeBusinessContactPhone(form.phone).e164
                        ? t('Se guardará como {value}',{value:normalizeBusinessContactPhone(form.phone).display})
                        : t("Elige el país y escribe el número del negocio.")
                    }
                  />
                </div>
                <label className="nb-min-field">
                  <span>{t("Instagram del negocio")}</span>
                  <input
                    value={form.instagram}
                    onChange={(e) => update("instagram", e.target.value)}
                    onBlur={() => update("instagram", normalizeInstagramHandle(form.instagram))}
                    placeholder="Ej: @studioglowpa"
                    autoComplete="off"
                  />
                  {normalizeInstagramHandle(form.instagram) ? (
                    <small className="nb-min-field-hint">{t('Se guardará como {value}',{value:'@'+normalizeInstagramHandle(form.instagram)})}</small>
                  ) : (
                    <small className="nb-min-field-hint">{t("Opcional, pero recomendado para el perfil público.")}</small>
                  )}
                </label>
              </div>
            </div>
          ) : null}

          {step === 2 ? (
            <div className="nb-min-question">
              <h2>{t("Revisa tus datos")}</h2><div className="nb-review-edit"><button type="button" onClick={()=>changeStep(0)}>{t("Editar negocio")}</button><button type="button" onClick={()=>changeStep(1)}>{t("Editar ubicación")}</button></div>
              <p className="nb-min-soft-copy">{t("Tu negocio quedará privado mientras lo preparas. Después agregas logo, servicios y horarios, y lo envías a revisión de Loop para publicarlo.")}</p>
              <div className="nb-min-review">
                <div><span>{t("Nombre")}</span><strong>{sanitize(form.name) || "—"}</strong></div>
                <div><span>{t("País y ciudad")}</span><strong>{getMarket(form.countryCode).name}{form.city ? ` · ${form.city}` : ""}</strong></div>
                <div><span>{t("Horario y moneda")}</span><strong>{zoneLabel(form.timezone)} · {getMarket(form.countryCode).currency}</strong></div>
                <div><span>{t("Tipo")}</span><strong>{t(selectedType?.label || '') || "—"}</strong></div>
                <div><span>{t("Atención")}</span><strong>{t(selectedMode?.label || '') || "—"}</strong></div>
                <div><span>{t("Zona")}</span><strong>{form.serviceMode === "delivery" ? (form.coverageText || "—") : form.serviceMode === "hybrid" ? `${form.locationText} · A domicilio: ${form.coverageText}` : (form.locationText || "—")}</strong></div>
                <div><span>{t("Teléfono / WhatsApp")}</span><strong>{normalizeBusinessContactPhone(form.phone).display || "—"}</strong></div>
                <div><span>{t("Equipo")}</span><strong>{form.teamMode === "team" ? t("Con un equipo") : t("Por mi cuenta")}</strong></div>
              </div>
              {!currentUser ? (
                <div className="nb-min-login-note">
                  Para guardar tu negocio, inicia sesión. Guardaremos este avance.
                </div>
              ) : null}
            </div>
          ) : null}

          {step === 0 && !currentUser && <p className="nb-min-field-hint">{t("A continuación crearás tu cuenta o podrás iniciar sesión. Conservaremos este avance en tu dispositivo.")}</p>}
          <footer className="nb-min-actions">
            <button type="button" className="nb-min-secondary" onClick={() => step === 0 ? navigate(-1) : back()} disabled={saving}>
              {step === 0 ? t("Cancelar") : t("Atrás")}
            </button>
            <button type="button" className="nb-min-primary" onClick={step < STEPS.length - 1 ? next : handleCreate} disabled={saving}>
              {continueLabel}
            </button>
          </footer>
        </section>
      </section>
    </main>
  );
}
